/**
 * Per-layout feature lists WITHOUT per-layout storage.
 *
 * Before: `featuresList = {Layout1: Part[], ..., LayoutN: Part[]}` and every not-in-visualizer feature
 * (or RoomLayout "ALL") was pushed into EVERY layout array (6 kvScene, 20 bvLook). Features with a RoomLayout
 * but without s3Bindings were even pushed twice into their own layout.
 *
 * Now every Part is stored ONCE (`all`, load order) with a bit mask of the layouts it belongs to.
 * `list.Layout3` is still a real (enumerable) property, so all legacy code keeps working:
 * `featuresList[layoutName]`, `Object.keys(featuresList)`, `featuresList[name].find(...)`.
 * The array for a layout is built on first read (same order as before: load order) and cached for the
 * most recently used layouts only; new parts are appended to the cached arrays, so the cache never needs
 * a rebuild while features stream in.
 *
 * Assigning `featuresList[layoutName] = [...]` (showerTubCombo wall configurations) is supported: that layout
 * then returns the assigned array, and parts added later for that layout are pushed into it (old behaviour).
 */

type AnyPart = object;

const MAX_CACHED_LAYOUTS = 3; // current layout + a couple of recently visited ones

interface CacheEntry {
  layoutName: string;
  list: AnyPart[];
}

export default class LayoutFeaturesList {
  // NOTE: real fields are non-enumerable (defined in the constructor) so Object.keys() returns layout names only
  private readonly _layoutNames!: string[];
  private readonly _bitByLayout!: Map<string, number>;
  private readonly _all!: AnyPart[];
  private readonly _masks!: Map<AnyPart, number | Set<string>>;
  private readonly _cache!: CacheEntry[];
  private readonly _overrides!: Record<string, AnyPart[]>;
  private readonly _useSets!: boolean;
  private readonly _allMask!: number;

  [layoutName: string]: unknown;

  constructor(layoutNames: string[]) {
    const useSets = layoutNames.length > 30;
    const hidden = (key: string, value: unknown) => Object.defineProperty(this, key, {value, enumerable: false, writable: false});
    hidden("_layoutNames", layoutNames.slice());
    hidden("_bitByLayout", new Map(layoutNames.map((name, index) => [name, 1 << index])));
    hidden("_all", []);
    hidden("_masks", new Map());
    hidden("_cache", []);
    hidden("_overrides", {});
    hidden("_useSets", useSets);
    hidden("_allMask", useSets ? 0 : (layoutNames.length >= 31 ? -1 : (1 << layoutNames.length) - 1));
    for (const layoutName of layoutNames) {
      Object.defineProperty(this, layoutName, {
        enumerable: true,
        configurable: false,
        get: () => this.getForLayout(layoutName),
        set: (list: AnyPart[]) => this.setForLayout(layoutName, list),
      });
    }
  }

  /** layout names this list was created with (same as Object.keys(list)) */
  getLayoutNames(): string[] {
    return this._layoutNames;
  }

  /** every part exactly once, in load order. Do NOT mutate. */
  getAll(): AnyPart[] {
    return this._all;
  }

  /** number of unique parts */
  get size(): number {
    return this._all.length;
  }

  has(part: AnyPart): boolean {
    return this._masks.has(part);
  }

  isInLayout(part: AnyPart, layoutName: string): boolean {
    const mask = this._masks.get(part);
    if (mask === undefined) {
      return false;
    }
    if (mask instanceof Set) {
      return mask.has(layoutName);
    }
    const bit = this._bitByLayout.get(layoutName);
    return !!bit && (mask & bit) !== 0;
  }

  /**
   * Adds a part to the given layouts (or to every layout). Adding the same part again only extends its layouts.
   * @param part {object}
   * @param layoutNames {string[] | true} true = every layout
   */
  add(part: AnyPart, layoutNames: string[] | true): void {
    if (!part) {
      return;
    }
    const names = layoutNames === true ? this._layoutNames : layoutNames.filter(name => this._bitByLayout.has(name));
    if (!names.length) {
      return;
    }
    const previous = this._masks.get(part);
    const isNew = previous === undefined;
    if (isNew) {
      this._all.push(part);
    }
    let newLayouts: string[];
    if (this._useSets) {
      const set = (previous as Set<string>) || new Set<string>();
      newLayouts = names.filter(name => !set.has(name));
      newLayouts.forEach(name => set.add(name));
      this._masks.set(part, set);
    } else {
      let mask = (previous as number) || 0;
      newLayouts = [];
      for (const name of names) {
        const bit = this._bitByLayout.get(name);
        if ((mask & bit) === 0) {
          mask |= bit;
          newLayouts.push(name);
        }
      }
      this._masks.set(part, layoutNames === true ? this._allMask : mask);
    }
    // keep cached / assigned arrays current (appending keeps the load order)
    for (const name of newLayouts) {
      const override = this._overrides[name];
      if (override) {
        override.push(part);
      }
      for (let i = 0; i < this._cache.length; i++) {
        if (this._cache[i].layoutName === name && this._cache[i].list !== override) {
          if (isNew) {
            this._cache[i].list.push(part);
          } else {
            this._cache.splice(i, 1); // existing part joined a layout: order may differ, rebuild on next read
          }
          break;
        }
      }
    }
  }

  /**
   * Same content as the old `featuresList[layoutName]` array.
   * @param layoutName {string}
   * @returns {object[]} cached array - treat it as read only
   */
  getForLayout(layoutName: string): AnyPart[] {
    const override = this._overrides[layoutName];
    if (override) {
      return override;
    }
    for (let i = 0; i < this._cache.length; i++) {
      const entry = this._cache[i];
      if (entry.layoutName === layoutName) {
        if (i !== 0) { // move to front (most recently used)
          this._cache.splice(i, 1);
          this._cache.unshift(entry);
        }
        return entry.list;
      }
    }
    const list: AnyPart[] = [];
    if (this._useSets) {
      for (let i = 0; i < this._all.length; i++) {
        if ((this._masks.get(this._all[i]) as Set<string>).has(layoutName)) {
          list.push(this._all[i]);
        }
      }
    } else {
      const bit = this._bitByLayout.get(layoutName);
      if (bit) {
        for (let i = 0; i < this._all.length; i++) {
          if (((this._masks.get(this._all[i]) as number) & bit) !== 0) {
            list.push(this._all[i]);
          }
        }
      }
    }
    this._cache.unshift({layoutName, list});
    if (this._cache.length > MAX_CACHED_LAYOUTS) {
      this._cache.pop();
    }
    return list;
  }

  /** legacy `featuresList[layoutName] = [...]` */
  setForLayout(layoutName: string, list: AnyPart[]): void {
    const safeList = Array.isArray(list) ? list : [];
    this._overrides[layoutName] = safeList;
    for (const part of safeList) {
      if (part && !this._masks.has(part)) {
        this._all.push(part);
        this._masks.set(part, this._useSets ? new Set<string>() : 0); // membership comes from the override array
      }
    }
  }
}
